import {
  UserAgent,
  Inviter,
  Registerer,
  RegistererState,
  SessionState,
} from 'sip.js';
import VoiceAPI from './voiceAPIClient';
import {
  setVoiceUiState,
  setMicDenied,
  showMicDeniedGuide,
  notifyVoiceRegistrationUp,
  notifyVoiceRegistrationDown,
} from './voiceBackgroundRegister';

// Tạo CustomEvent giống Twilio client để component/store lắng nghe 'call:disconnected'
const createCallDisconnectedEvent = () => new CustomEvent('call:disconnected');

// Tạo CustomEvent 'call:incoming' để useCallSession/store hiện pop-up khi có INVITE
// đến trực tiếp qua WSS (Model A / hybrid). detail chứa caller number + callId để UI hiển thị.
const createCallIncomingEvent = detail =>
  new CustomEvent('call:incoming', { detail });

// ID cố định cho <audio> element dùng phát remote audio (nghe đầu bên kia)
const REMOTE_AUDIO_ELEMENT_ID = 'sip-voice-remote-audio';

// Guard U1: thời hạn "vũ trang" auto-accept sau khi POST conference trả bridge_id.
const ARM_TTL_MS = 45000;
// Server originate agent leg NGAY TRONG request POST conference → INVITE có thể
// tới browser TRƯỚC khi HTTP response (chứa bridge_id) về. Giữ INVITE im lặng
// tối đa chừng này chờ armAutoAccept khớp; hết hạn → 486.
const PENDING_INVITE_GRACE_MS = 5000;

// Lỗi getUserMedia do quyền (khác lỗi thiết bị/ICE)
const MIC_PERMISSION_ERRORS = [
  'NotAllowedError',
  'PermissionDeniedError',
  'SecurityError',
];

class SipVoiceClient extends EventTarget {
  constructor() {
    super();
    // userAgent = tương đương "device" của Twilio (UserAgent quản lý WebSocket + SIP stack)
    this.userAgent = null;
    // registerer = đối tượng REGISTER extension lên Asterisk
    this.registerer = null;
    // activeConnection = session đang gọi (Inviter/Invitation) — giữ tên giống Twilio API surface
    this.activeConnection = null;
    // incomingSession = INVITE đến chờ agent accept (fallback acceptIncoming thủ công)
    this.incomingSession = null;
    // audio element dùng để phát remote stream
    this.remoteAudioElement = null;
    // ice_servers lấy từ token, gắn vào peerConnectionConfiguration mỗi lần tạo session
    this.iceServers = [];
    // realm dùng để dựng URI target khi gọi ra (sip:${to}@realm)
    this.realm = null;
    this.initialized = false;
    this.inboxId = null;
    // Guard U1: bridge_id vừa Join thành công (chỉ INVITE khớp mới được accept)
    this.armedBridgeId = null;
    this.armTimer = null;
    // INVITE giữ chờ armAutoAccept (đua INVITE ↔ HTTP response POST conference)
    this.pendingInvite = null;
    // Đang teardown chủ động → bỏ qua event Unregistered/onDisconnect (không retry)
    this.tearingDown = false;
  }

  // ===== Guard U1: arm/disarm auto-accept theo bridge_id =====

  // Gọi khi POST conference trả về bridge_id (wire tại voiceClientProxy).
  // Trong ARM_TTL_MS, INVITE có header X-CW-Bridge-Id khớp sẽ được auto-accept.
  armAutoAccept(bridgeId) {
    if (!bridgeId) return;
    if (this.armTimer) clearTimeout(this.armTimer);
    this.armedBridgeId = bridgeId;
    this.armTimer = setTimeout(() => this.disarmAutoAccept(), ARM_TTL_MS);

    // INVITE đã tới TRƯỚC khi arm (race) và đang giữ → xử lý ngay.
    if (this.pendingInvite && this.pendingInvite.bridgeId === bridgeId) {
      const { invitation } = this.consumePendingInvite();
      this.acceptBridgeInvite(invitation);
    }
  }

  disarmAutoAccept() {
    if (this.armTimer) clearTimeout(this.armTimer);
    this.armTimer = null;
    this.armedBridgeId = null;
  }

  // Giữ INVITE im lặng (KHÔNG UI/chuông) chờ armAutoAccept trong grace window.
  holdPendingInvite(invitation, bridgeId) {
    this.clearPendingInvite(true); // INVITE giữ trước đó (nếu có) → 486
    const timer = setTimeout(() => {
      // Hết grace mà không có Join khớp → không phải leg của browser này → 486.
      this.clearPendingInvite(true);
    }, PENDING_INVITE_GRACE_MS);
    this.pendingInvite = { invitation, bridgeId, timer };
    // Caller/ARI huỷ INVITE trong lúc giữ → dọn, không reject thừa.
    invitation.stateChange.addListener(state => {
      if (
        state === SessionState.Terminated &&
        this.pendingInvite?.invitation === invitation
      ) {
        this.clearPendingInvite(false);
      }
    });
  }

  consumePendingInvite() {
    const pending = this.pendingInvite;
    if (pending) clearTimeout(pending.timer);
    this.pendingInvite = null;
    return pending || {};
  }

  clearPendingInvite(reject) {
    const pending = this.consumePendingInvite();
    if (reject && pending.invitation) this.safeReject(pending.invitation);
  }

  // Reject 486 an toàn theo state (chỉ hợp lệ khi INVITE chưa được trả lời)
  safeReject(invitation) {
    try {
      if (invitation?.state === SessionState.Initial) {
        invitation.reject({ statusCode: 486 });
      }
    } catch (e) {
      /* session đã terminated — bỏ qua */
    }
  }

  // ===== Media helpers =====

  // Tạo (hoặc tái tạo) 1 <audio> element ẩn để gắn remote MediaStream vào
  ensureRemoteAudioElement() {
    // Nếu đã có sẵn thì tái sử dụng, tránh tạo nhiều element rác
    let el = document.getElementById(REMOTE_AUDIO_ELEMENT_ID);
    if (!el) {
      el = document.createElement('audio');
      el.id = REMOTE_AUDIO_ELEMENT_ID;
      el.autoplay = true;
      el.hidden = true;
      document.body.appendChild(el);
    }
    this.remoteAudioElement = el;
    return el;
  }

  // Lấy PeerConnection thật từ sessionDescriptionHandler của SIP.js
  getPeerConnection(session) {
    const sdh = session?.sessionDescriptionHandler;
    // SIP.js default SessionDescriptionHandler expose .peerConnection
    return sdh?.peerConnection || null;
  }

  // Gắn các remote track (audio bên kia) vào <audio> element để nghe
  attachRemoteMedia(session) {
    const pc = this.getPeerConnection(session);
    if (!pc) return;
    const remoteStream = new MediaStream();
    pc.getReceivers().forEach(receiver => {
      if (receiver.track) remoteStream.addTrack(receiver.track);
    });
    const el = this.ensureRemoteAudioElement();
    el.srcObject = remoteStream;
    // Một số trình duyệt cần play() thủ công dù đã autoplay
    const playPromise = el.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        /* autoplay có thể bị chặn — bỏ qua, user gesture sẽ mở lại */
      });
    }
  }

  // Theo dõi vòng đời session; khi kết thúc thì dọn dẹp + phát event disconnected
  bindSessionStateListener(session) {
    session.stateChange.addListener(state => {
      switch (state) {
        case SessionState.Established:
          // Đàm thoại đã kết nối → gắn audio remote để nghe
          this.attachRemoteMedia(session);
          break;
        case SessionState.Terminated:
          // Session kết thúc (bye/cancel/từ chối) → cùng handler với Twilio onDisconnect
          this.onDisconnect();
          break;
        default:
          break;
      }
    });
  }

  // Lấy số/định danh người gọi từ INVITE đến (remoteIdentity.uri.user)
  extractCaller(invitation) {
    try {
      return invitation?.remoteIdentity?.uri?.user || null;
    } catch (e) {
      return null;
    }
  }

  // ===== Inbound INVITE (Model B — GUARD U1) =====
  //
  // Endpoint 1001 dùng CHUNG (max_contacts=3) → INVITE do ARI originate đổ tới
  // MỌI browser đã REGISTER. Chỉ browser vừa POST Join thành công (được arm với
  // bridge_id) mới auto-accept; còn lại 486 và KHÔNG hiện UI/chuông.
  handleIncomingInvite = invitation => {
    // Đang đàm thoại → bận, từ chối mọi INVITE mới (không chuông chồng).
    if (this.activeConnection) {
      this.safeReject(invitation);
      return;
    }

    const bridgeId =
      invitation?.request?.getHeader?.('X-CW-Bridge-Id') || null;

    // Không có header định tuyến → không phải agent leg của bridge nào → 486.
    if (!bridgeId) {
      this.safeReject(invitation);
      return;
    }

    if (this.armedBridgeId) {
      if (bridgeId === this.armedBridgeId) {
        this.acceptBridgeInvite(invitation);
      } else {
        // Khớp bridge KHÁC (Join của agent khác) → không phải của mình.
        this.safeReject(invitation);
      }
      return;
    }

    // Chưa arm: có thể HTTP response POST conference chưa về (server originate
    // trước khi render response). Giữ im lặng chờ armAutoAccept trong grace.
    this.holdPendingInvite(invitation, bridgeId);
  };

  // Auto-accept INVITE bridge đã qua guard U1. Xin quyền micro đúng lúc accept;
  // denied → báo mic_denied + hướng dẫn (không nuốt lỗi âm thầm).
  async acceptBridgeInvite(invitation) {
    if (!invitation) return;
    if (this.activeConnection) {
      this.safeReject(invitation);
      return;
    }
    // Mỗi Join chỉ nhận đúng 1 leg → tiêu thụ arm ngay.
    this.disarmAutoAccept();
    this.bindSessionStateListener(invitation);
    // Dự phòng nguồn caller info cho UI (pop-up chính do backend message.created).
    this.dispatchEvent(
      createCallIncomingEvent({
        caller: this.extractCaller(invitation),
        callId: invitation?.request?.callId || null,
      })
    );

    const micOk = await this.ensureMicrophoneAccess();
    if (!micOk) {
      // Không có micro thì không thể đàm thoại → trả 486 cho tổng đài biết.
      this.safeReject(invitation);
      return;
    }

    try {
      await invitation.accept({
        sessionDescriptionHandlerOptions: {
          constraints: { audio: true, video: false },
        },
      });
      this.activeConnection = invitation;
      this.incomingSession = null;
    } catch (e) {
      // accept lỗi (ICE/SDP/media) → đẩy ra badge, giữ incomingSession để agent
      // thử acceptIncoming() thủ công.
      this.incomingSession = invitation;
      setVoiceUiState('error', `Không nhận được cuộc gọi: ${e?.message || e}`);
    }
  }

  // Xin quyền micro tại thời điểm nhận/gọi (probe stream đóng ngay — SIP.js tự
  // getUserMedia lại khi accept/invite). Trả false nếu không dùng được micro.
  async ensureMicrophoneAccess() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      setMicDenied(false);
      return true;
    } catch (e) {
      if (MIC_PERMISSION_ERRORS.includes(e?.name)) {
        setMicDenied(true);
        showMicDeniedGuide();
      } else {
        setVoiceUiState(
          'error',
          `Không truy cập được microphone: ${e?.message || e}`
        );
      }
      return false;
    }
  }

  // ===== Registerer / transport state → badge US2 =====

  get isRegistered() {
    return this.registerer?.state === RegistererState.Registered;
  }

  handleRegistererState(state) {
    if (this.tearingDown) return;
    switch (state) {
      case RegistererState.Registered:
        notifyVoiceRegistrationUp();
        break;
      case RegistererState.Unregistered:
        notifyVoiceRegistrationDown('Tổng đài từ chối/huỷ đăng ký SIP');
        break;
      case RegistererState.Terminated:
        notifyVoiceRegistrationDown('Phiên đăng ký SIP kết thúc');
        break;
      default:
        break;
    }
  }

  handleTransportDown(error) {
    if (this.tearingDown) return;
    notifyVoiceRegistrationDown(
      error
        ? `WebSocket tổng đài đứt: ${error.message || error}`
        : 'WebSocket tổng đài đóng'
    );
  }

  // ===== UA lifecycle =====

  // Dựng UserAgent + REGISTER extension từ sip_credentials thô (dùng chung cho
  // cả token cũ US2 lẫn shift_start GĐ2-ACD). Tách riêng để KHÔNG lặp code
  // (DRY) giữa createUserAgent (token inbox) và registerWithCredentials (ca ACD).
  async buildUserAgentFromCredentials(sip, iceServers) {
    if (!sip || !sip.ws_server || !sip.sip_uri) {
      throw new Error('Invalid token');
    }

    // RESET cờ teardown (bug code 1006): destroyDevice() đặt tearingDown=true nhưng
    // KHÔNG reset → khi dựng UA MỚI (vào ca), handleRegistererState bỏ qua mọi state
    // change (Registered/Unregistered) của UA mới vì tearingDown còn true → badge kẹt
    // + register coi như thất bại. Phải reset TRƯỚC khi dựng UA mới.
    this.tearingDown = false;
    this.iceServers = Array.isArray(iceServers) ? iceServers : [];
    this.realm = sip.realm;

    // uri = danh tính của agent trên Asterisk (sip:username@realm)
    const uri = UserAgent.makeURI(sip.sip_uri);
    if (!uri) throw new Error('Invalid SIP URI');

    // Khởi tạo UserAgent: transport WebSocket (ws_server) + thông tin xác thực
    this.userAgent = new UserAgent({
      uri,
      transportOptions: {
        server: sip.ws_server, // wss://.../ws
      },
      authorizationUsername: sip.username,
      authorizationPassword: sip.password,
      // Cấu hình ICE (STUN/TURN) cho mọi session tạo bởi UA này.
      // iceGatheringTimeout: SIP.js mặc định 5000ms → 5 GIÂY IM LẶNG sau khi Nhận
      // (browser đợi gom đủ candidate mới gửi SDP answer). Asterisk có IP public
      // + rtp_symmetric=yes nên 500ms là đủ (học địa chỉ từ RTP đến).
      sessionDescriptionHandlerFactoryOptions: {
        iceGatheringTimeout: 500,
        peerConnectionConfiguration: {
          iceServers: this.iceServers,
        },
      },
      delegate: {
        // onInvite: nhận INVITE đến (inbound) — qua guard U1
        onInvite: this.handleIncomingInvite,
        // WSS đứt → báo badge + kích hoạt retry (voiceBackgroundRegister)
        onDisconnect: error => this.handleTransportDown(error),
      },
    });

    // Start UA (mở WebSocket) rồi REGISTER extension
    await this.userAgent.start();
    this.registerer = new Registerer(this.userAgent);
    // Kết quả đăng ký thật (200/lỗi/mất) phát qua stateChange → badge US2.
    this.registerer.stateChange.addListener(state =>
      this.handleRegistererState(state)
    );
    await this.registerer.register();

    this.initialized = true;
    return this.userAgent;
  }

  // Khởi tạo UserAgent + REGISTER extension nền. Dùng chung cho cả đăng ký
  // thường trực (registerBackground) lẫn initializeDevice trước khi gọi ra.
  async createUserAgent(inboxId) {
    const response = await VoiceAPI.getToken(inboxId);
    const { sip_credentials: sip, ice_servers: iceServers } = response || {};
    await this.buildUserAgentFromCredentials(sip, iceServers);
    this.inboxId = inboxId;
    return this.userAgent;
  }

  // GĐ2-ACD: đăng ký MEDIA 1 lần cho cả ca trực bằng sip_credentials trả về từ
  // POST voice/agent/shift/start (KHÁC nguồn credential với token theo inbox
  // của luồng legacy). Không gắn inboxId (ACD không đăng ký theo inbox cụ thể).
  // Đã có UA sống (register cũ) → teardown trước khi dựng lại cho ca mới.
  async registerWithCredentials(sipCredentials, iceServers) {
    // Đợi teardown UA cũ XONG HẲN (stop async đóng WSS) trước khi dựng UA mới —
    // tránh WSS của UA cũ đóng đè lên UA mới đang connect (code 1006).
    if (this.userAgent) {
      this.destroyDevice();
      await new Promise(r => setTimeout(r, 150)); // nhường tick cho stop() đóng transport
    }
    await this.buildUserAgentFromCredentials(sipCredentials, iceServers);
    this.inboxId = null;
    return this.userAgent;
  }

  // Đăng ký SIP nền THƯỜNG TRỰC: gọi ngay khi agent vào dashboard để endpoint
  // luôn ONLINE, sẵn sàng nhận INVITE đến. Idempotent khi đăng ký còn SỐNG;
  // đăng ký đã chết (WSS đứt/terminated) → teardown + dựng lại (tự phục hồi).
  async registerBackground(inboxId) {
    if (this.initialized && this.userAgent && this.inboxId === inboxId) {
      const state = this.registerer?.state;
      if (
        state === RegistererState.Registered ||
        state === RegistererState.Initial
      ) {
        return this.userAgent;
      }
    }
    // Đổi inbox / chưa có UA / đăng ký chết → dựng lại (teardown UA cũ nếu có)
    if (this.userAgent) this.destroyDevice();
    return this.createUserAgent(inboxId);
  }

  // Giữ API surface cũ. Trước khi gọi ra, đảm bảo đã đăng ký nền (không teardown
  // đăng ký thường trực). Nếu chưa đăng ký hoặc khác inbox → registerBackground.
  async initializeDevice(inboxId) {
    return this.registerBackground(inboxId);
  }

  // Getter giống Twilio: có session đang hoạt động hay không
  get hasActiveConnection() {
    return !!this.activeConnection;
  }

  // Bật/tắt mute bằng cách enable/disable track audio của sender
  setMuted(shouldMute) {
    if (!this.activeConnection) return false;
    const pc = this.getPeerConnection(this.activeConnection);
    if (!pc) return false;
    pc.getSenders().forEach(sender => {
      if (sender.track && sender.track.kind === 'audio') {
        // enabled = true nghĩa là KHÔNG mute → đảo ngược shouldMute
        sender.track.enabled = !shouldMute;
      }
    });
    return shouldMute;
  }

  // Kết thúc cuộc gọi phía client: gửi BYE/CANCEL tuỳ trạng thái session
  endClientCall() {
    this.terminateSession(this.activeConnection);
    this.activeConnection = null;
  }

  // Helper: terminate 1 session an toàn theo state (SIP.js yêu cầu đúng method theo state)
  terminateSession(session) {
    if (!session) return;
    switch (session.state) {
      case SessionState.Initial:
      case SessionState.Establishing:
        // Chưa established: nếu là Inviter thì cancel, nếu Invitation thì reject
        if (typeof session.cancel === 'function') {
          session.cancel();
        } else if (typeof session.reject === 'function') {
          session.reject();
        }
        break;
      case SessionState.Established:
        // Đang đàm thoại → gửi BYE
        session.bye();
        break;
      default:
        // Terminating/Terminated: không làm gì
        break;
    }
  }

  // Huỷ toàn bộ UA + dọn state (giống Twilio destroyDevice)
  destroyDevice() {
    // Teardown chủ động → chặn handler state khỏi báo lỗi/retry nhầm
    this.tearingDown = true;
    this.disarmAutoAccept();
    this.clearPendingInvite(true);
    this.terminateSession(this.activeConnection);
    this.terminateSession(this.incomingSession);

    if (this.registerer) {
      // unregister best-effort, không chặn nếu lỗi
      try {
        this.registerer.unregister();
      } catch (e) {
        /* bỏ qua lỗi unregister khi teardown */
      }
    }
    if (this.userAgent) {
      // stop() đóng WebSocket transport
      try {
        this.userAgent.stop();
      } catch (e) {
        /* bỏ qua lỗi stop khi teardown */
      }
    }

    if (this.remoteAudioElement) {
      this.remoteAudioElement.srcObject = null;
    }

    this.userAgent = null;
    this.registerer = null;
    this.activeConnection = null;
    this.incomingSession = null;
    this.iceServers = [];
    this.realm = null;
    this.initialized = false;
    this.inboxId = null;
    this.tearingDown = false;
  }

  // Gọi ra: tạo Inviter gửi INVITE tới target (to = extension/số đích).
  // INBOUND asterisk: to = '' (server originate leg về) → trả null, không INVITE.
  async joinClientCall({ to, conversationId, callSid }) {
    if (!this.userAgent || !this.initialized || !to) return null;
    // Đã có cuộc gọi đang diễn ra → trả về session hiện tại (giống Twilio)
    if (this.activeConnection) return this.activeConnection;

    // Xin quyền micro trước khi INVITE; denied → báo hướng dẫn + lỗi thật
    // (useCallSession bắt và hiện alert, không thất bại âm thầm).
    const micOk = await this.ensureMicrophoneAccess();
    if (!micOk) throw new Error('Chưa cấp quyền microphone');

    // Dựng URI target: sip:${to}@realm
    const targetUri = UserAgent.makeURI(`sip:${to}@${this.realm}`);
    if (!targetUri) return null;

    // Đưa metadata (conversation/call_sid) vào SIP header X- để backend nhận diện
    const extraHeaders = [
      'X-Is-Agent: true',
      `X-Conversation-Id: ${conversationId != null ? conversationId : ''}`,
      `X-Call-Sid: ${callSid != null ? callSid : ''}`,
    ];

    const inviter = new Inviter(this.userAgent, targetUri, {
      // Chỉ audio: gửi audio, nhận audio
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
      },
      extraHeaders,
    });

    // Theo dõi state để gắn remote media + xử lý disconnect
    this.bindSessionStateListener(inviter);

    // Gửi INVITE (bắt đầu gọi)
    await inviter.invite();

    this.activeConnection = inviter;
    return inviter;
  }

  // Chấp nhận INVITE đến thủ công (fallback khi auto-accept lỗi) — agent bấm nghe
  async acceptIncoming() {
    if (!this.incomingSession) return null;
    // Đã có cuộc gọi khác đang diễn ra → không accept
    if (this.activeConnection) return this.activeConnection;

    // Bấm Nhận mà mic denied → hướng dẫn cấp quyền, không throw âm thầm.
    const micOk = await this.ensureMicrophoneAccess();
    if (!micOk) return null;

    const invitation = this.incomingSession;
    await invitation.accept({
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
      },
    });

    this.activeConnection = invitation;
    this.incomingSession = null;
    return invitation;
  }

  // Thông tin cuộc gọi đến đang chờ (caller number) để UI hiển thị pop-up.
  // Trả null nếu không có INVITE đến đang chờ accept.
  getIncomingInfo() {
    if (!this.incomingSession) return null;
    const inv = this.incomingSession;
    return {
      caller: this.extractCaller(inv),
      callId: inv?.request?.callId || null,
    };
  }

  // Handler chung khi cuộc gọi kết thúc: clear activeConnection + dispatch event.
  // QUAN TRỌNG: KHÔNG unregister UA ở đây — giữ đăng ký thường trực để tiếp tục
  // nhận cuộc gọi đến. Chỉ dọn session state (active + incoming đã kết thúc).
  onDisconnect = () => {
    this.activeConnection = null;
    // INVITE đến bị caller huỷ/từ chối trước khi accept → dọn để pop-up biến mất.
    this.incomingSession = null;
    this.dispatchEvent(createCallDisconnectedEvent());
  };
}

export default new SipVoiceClient();
